
import {
  computed, defineComponent, onBeforeMount, reactive,
} from 'vue';
import { Modal } from 'ant-design-vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { IColumn } from '@/core/models/AntDesign/IColumn';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import Icone from '@/core/components/Icone.vue';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import UtilitarioFinanceiro from '@/core/utilitarios/UtilitarioFinanceiro';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import NotaFiscalDetalhePagamentoModal from '@/components/Fiscal/NotasFiscais/NotaFiscalDetalhePagamentoModal.vue';
import { IFormaPagamento } from '@/models/Entidades/Cadastros/Financeiro/IFormaPagamento';
import { ICteDetalhePagamento } from '@/models/Entidades/Compras/ConhecimentoTransporte/ICteDetalhePagamento';

export default defineComponent({
  name: 'CteDetalheFinanceiro',
  props: {
    empresa: {
      type: Number,
      required: true,
    },
    editavel: {
      type: Boolean,
      default: true,
    },
    detalhesPagamento: {
      type: Array as () => ICteDetalhePagamento[],
      required: true,
    },
  },
  components: {
    CampoNumerico,
    MensagemSemDados,
    Icone,
    NotaFiscalDetalhePagamentoModal,
  },
  emits: ['update:detalhesPagamento'],
  setup(props, { emit }) {
    const { telaBase } = useTelaBase();
    const state = reactive({
      colunasGrade: [] as IColumn[],
      detalheSelecionado: {} as ICteDetalhePagamento,
      detalhesSelecionados: [] as ICteDetalhePagamento[],
      exibirLancamento: false,
      indexSelecionado: -1,
      telaMobile: false,
      formaPagamento: {} as IFormaPagamento,
    });

    const computedDetalhesPagamento = computed({
      get: () => props.detalhesPagamento,
      set: (val: ICteDetalhePagamento[]) => emit('update:detalhesPagamento', val),
    });

    function preencherColunas() {
      if (state.telaMobile) {
        state.colunasGrade = [
          {
            title: 'Código', dataIndex: 'codigo', key: 'codigo', position: 1, visible: false,
          },
          {
            title: 'Detalhe', dataIndex: 'numeroTitulo', key: 'detalheMobile', position: 2, visible: true,
          },
        ];
      } else {
        state.colunasGrade = [
          {
            title: 'Código', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
          },
          {
            title: 'N.Identificação', dataIndex: 'numeroTitulo', key: 'numeroTitulo', position: 1, visible: true, width: 130, ellipsis: true,
          },
          {
            title: 'T.Documento', dataIndex: 'descricaoTipoDocumentoFinanceiro', key: 'descricaoTipoDocumentoFinanceiro', position: 2, visible: true, width: 130, ellipsis: true,
          },
          {
            title: 'Vencimento', dataIndex: 'data', key: 'data', position: 3, visible: true, width: 110, align: 'center', ellipsis: true,
          },
          {
            title: 'Valor', dataIndex: 'valorPagamento', key: 'valorPagamento', position: 4, visible: true, width: 100, align: 'right', ellipsis: true,
          },
          {
            title: 'Acréscimos', dataIndex: 'acrescimos', key: 'acrescimos', position: 5, visible: true, width: 100, align: 'right', ellipsis: true,
          },
          {
            title: 'Outras Taxas', dataIndex: 'outrasTaxas', key: 'outrasTaxas', position: 6, visible: true, width: 110, align: 'right', ellipsis: true,
          },
          {
            title: 'Valor Total', dataIndex: 'valorTotal', key: 'valorTotal', position: 7, visible: true, width: 120, align: 'right', ellipsis: true,
          },
          {
            title: 'Conta', dataIndex: 'descricaoConta', key: 'descricaoConta', position: 8, visible: true, ellipsis: true,
          },
          {
            title: 'Categoria', dataIndex: 'nomePlanoContaCategoria', key: 'nomePlanoContaCategoria', position: 9, visible: true, width: 230, ellipsis: true,
          },
          {
            title: 'Ações', key: 'acoes', position: 10, visible: true, width: 70, align: 'center', ellipsis: true,
          },
        ];
      }
    }

    function adicionarDetalhe() {
      state.indexSelecionado = -1;
      state.detalhesSelecionados = [];
      state.detalheSelecionado = {
        codigo: 0,
        codigoCte: 0,
        codigoTipoDocumentoFinanceiro: 0,
        descricaoTipoDocumentoFinanceiro: '',
        codigoConta: 0,
        descricaoConta: '',
        codigoPlanoContaCategoria: 0,
        nomePlanoContaCategoria: '',
        codigoTituloFinanceiro: 0,
        statusTituloFinanceiro: 1 as any,
        numeroTitulo: '',
        parcela: 1,
        data: '',
        valorPagamento: 0,
        acrescimos: 0,
        outrasTaxas: 0,
        valorTotal: 0,
        observacoes: '',
        gerarCarteira: false,
        gerarCaixa: false,
        anexos: [],
        centrosCustos: [],
        alterado: false,
      };
      state.exibirLancamento = true;
    }

    function editarDetalhe(index: number) {
      state.indexSelecionado = index;
      state.detalheSelecionado = UtilitarioGeral.clonarObjeto(computedDetalhesPagamento.value[index]);
      state.detalhesSelecionados = [UtilitarioGeral.clonarObjeto(computedDetalhesPagamento.value[index])];
      state.exibirLancamento = true;
    }

    function removerDetalhe(index: number) {
      computedDetalhesPagamento.value.splice(index, 1);
    }

    function atualizaParcelas(qtdParcelas: number, valorTotal: number) {
      const parcelas = UtilitarioFinanceiro.calcularParcelas(valorTotal, qtdParcelas - 1);
      for (let i = 0; i < qtdParcelas - 1; i += 1) {
        computedDetalhesPagamento.value[i].valorPagamento = parcelas[i].valueOf();
        computedDetalhesPagamento.value[i].valorTotal = parcelas[i].valueOf();
        computedDetalhesPagamento.value[i].alterado = true;
      }
    }

    function removerDetalheComParcelas(index: number, removerTudo: boolean) {
      const codigoTipoDocumentoFinanceiro = computedDetalhesPagamento.value[index].codigoTipoDocumentoFinanceiro;
      computedDetalhesPagamento.value.splice(index, 1);
      if (removerTudo) {
        for (let i = computedDetalhesPagamento.value.length - 1; i >= 0; i -= 1) {
          if (computedDetalhesPagamento.value[i].codigoTipoDocumentoFinanceiro === codigoTipoDocumentoFinanceiro) {
            computedDetalhesPagamento.value.splice(i, 1);
          }
        }
      }
    }

    function excluirDetalhe(index: number) {
      let qtdParcelas = 0;
      let valorTotal = 0;

      computedDetalhesPagamento.value.forEach((detalhe) => {
        if (detalhe.codigoTipoDocumentoFinanceiro === computedDetalhesPagamento.value[index].codigoTipoDocumentoFinanceiro) {
          qtdParcelas += 1;
          valorTotal += detalhe.valorTotal;
        }
      });

      if (qtdParcelas > 1) {
        Modal.confirm({
          title: 'Você realmente deseja excluir o detalhe selecionado e os relacionados?',
          content: `O detalhe ${computedDetalhesPagamento.value[index].numeroTitulo} faz parte de um parcelamento de ${qtdParcelas} parcelas!`,
          okText: 'Sim',
          okType: 'danger',
          cancelText: 'Não',
          autoFocusButton: null,
          onOk: () => removerDetalheComParcelas(index, true),
          onCancel: () => {
            Modal.confirm({
              title: 'Deseja recalcular as parcelas?',
              content: 'O valor das parcelas será alterado de acordo com as parcelas restantes!',
              okText: 'Sim',
              okType: 'danger',
              cancelText: 'Não',
              autoFocusButton: null,
              onOk: () => {
                removerDetalheComParcelas(index, false);
                atualizaParcelas(qtdParcelas, valorTotal);
              },
              onCancel: () => {
                removerDetalheComParcelas(index, false);
              },
            });
          },
        });
      } else {
        Modal.confirm({
          title: 'Você realmente deseja excluir o detalhe selecionado?',
          content: `Detalhe Número: ${computedDetalhesPagamento.value[index].numeroTitulo || 'Sem título'}`,
          okText: 'Sim',
          okType: 'danger',
          cancelText: 'Não',
          autoFocusButton: null,
          onOk: () => removerDetalhe(index),
        });
      }
    }

    function normalizarDetalhe(detalhe: ICteDetalhePagamento): ICteDetalhePagamento {
      const valorPagamento = detalhe.valorPagamento ?? 0;
      const acrescimos = detalhe.acrescimos ?? 0;
      const outrasTaxas = detalhe.outrasTaxas ?? 0;
      return {
        ...detalhe,
        valorPagamento,
        acrescimos,
        outrasTaxas,
        valorTotal: valorPagamento + acrescimos + outrasTaxas,
        anexos: detalhe.anexos ?? [],
        centrosCustos: detalhe.centrosCustos ?? [],
      };
    }

    function confirmarDetalhes(detalhes: ICteDetalhePagamento[]) {
      if (!UtilitarioGeral.validaLista(detalhes)) {
        return;
      }

      detalhes.forEach((item) => {
        const detalhe = normalizarDetalhe(UtilitarioGeral.clonarObjeto(item));
        if (state.indexSelecionado >= 0 && detalhes.length === 1) {
          computedDetalhesPagamento.value[state.indexSelecionado] = detalhe;
        } else {
          computedDetalhesPagamento.value.push(detalhe);
        }
      });
    }

    function calcularTotal(): number {
      let total = 0;
      computedDetalhesPagamento.value.forEach((item) => {
        const valorPagamento = item.valorPagamento ?? 0;
        const acrescimos = item.acrescimos ?? 0;
        const outrasTaxas = item.outrasTaxas ?? 0;
        total += valorPagamento + acrescimos + outrasTaxas;
      });
      return total;
    }

    onBeforeMount(() => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      preencherColunas();
    });

    return {
      props,
      telaBase,
      state,
      UtilitarioData,
      UtilitarioMascara,
      computedDetalhesPagamento,
      adicionarDetalhe,
      editarDetalhe,
      excluirDetalhe,
      confirmarDetalhes,
      calcularTotal,
    };
  },
});
